import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import Table from '@app/src/components/Table'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import useSort from '@app/src/hooks/sorting'
import { useAssessmentTrends } from '@app/src/hooks/useAssessmentTrend'
import type Assessment from '@app/src/types/assessment'
import type { AssessmentTemplate, RiskStatus, RiskType } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import React from 'react'
import AssessmentDialogHeader from '../../../../Assessments/AssessmentDialogHeader'
import AssessmentDialogRow from '../../../../Assessments/AssessmentDialogRow'
import type { FilterGroup } from '../../../Filters/useFilters'
import { Operators } from '../../../Filters/useFilters'

type AssessmentModalTableProps = {
  assessmentTemplate: AssessmentTemplate
  activeLevel?: number
  periods?: string[]
  riskType?: RiskType
  riskStatus?: RiskStatus
  providerIds?: Array<number>
  filters?: FilterGroup[]
}

const AssessmentModalTable: React.FC<AssessmentModalTableProps> = ({
  assessmentTemplate,
  activeLevel,
  periods,
  riskType,
  riskStatus,
  providerIds,
  filters,
}) => {
  const { sorting, toggleSorting } = useSort()
  const actualFilters = filters?.length
    ? filters
    : [
        {
          name: 'assessmentTemplateId',
          filters: [
            {
              operator: Operators.EqualTo,
              value: assessmentTemplate.id,
            },
          ],
        },
        ...insertIf<FilterGroup>(Boolean(providerIds?.length), {
          name: 'providerId',
          filters: [
            {
              operator: Operators.In,
              value: providerIds,
            },
          ],
        }),
      ]

  const [page, pageSize, setPage, setPageSize] = usePaginationWithQuery(undefined, [filters, actualFilters])

  const {
    items: assessments,
    isLoading,
    count,
    isError,
  } = useFetchCollectionWithPost<Assessment>({
    key: FetchKey.AssessmentCollection,
    endpoint: endpoints.assessmentCollection,
    payload: {
      sort: sorting,
      pagination: { pageNumber: page, itemsPerPage: pageSize },
      filter: [
        ...actualFilters,
        ...insertIf<FilterGroup>(Boolean(activeLevel), {
          name: 'totalLevel',
          filters: [
            {
              operator: Operators.EqualTo,
              value: activeLevel,
            },
          ],
        }),
        ...insertIf<FilterGroup>(Boolean(periods), {
          name: 'periodName',
          filters: [
            {
              operator: Operators.In,
              value: periods,
            },
          ],
        }),
      ],
      include: ['provider', 'assessmentTemplate', 'provider.country', 'provider.providerOwners.user'],
    },
  })

  const { items: allAssessments } = useFetchCollectionWithPost<Assessment>({
    key: [FetchKey.AssessmentCollection, assessmentTemplate.id, 'trends'],
    endpoint: endpoints.assessmentCollection,
    payload: {
      filter: actualFilters,
      include: ['provider', 'assessmentTemplate', 'provider.providerOwners.user'],
      pagination: {
        pageNumber: 1,
        itemsPerPage: 9999,
      },
    },
    options: {
      enabled: Boolean(providerIds?.length || filters?.length),
      staleTime: ONE_MINUTE_MS,
    },
  })

  const { hasAnyTrends } = useAssessmentTrends(assessments || [], allAssessments || [])

  return (
    <>
      <Table<Assessment>
        HeaderComponent={() => (
          <AssessmentDialogHeader
            toggleSorting={toggleSorting}
            activeSorting={sorting}
            assessmentTemplate={assessmentTemplate}
            riskType={riskType}
            showTrendColumn={hasAnyTrends}
          />
        )}
        RowComponent={({ row }) => (
          <AssessmentDialogRow
            row={row}
            riskStatus={riskStatus}
            allAssessments={allAssessments || []}
            showTrendColumn={hasAnyTrends}
          />
        )}
        data={assessments}
        isLoading={isLoading}
        count={count}
        isError={isError}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
      />
    </>
  )
}

export default AssessmentModalTable
